import React, { useMemo } from "react";
import { Eye, Edit2, Plus, UserX, UserCheck, Trash2, Send, Download } from "lucide-react";
import { DataTable, Avatar, Badge, TableActionButton } from "../../../../components/ui";
import { STATUS_VARIANT_MAP, DESIGN_STAGES } from "@/constants/globalConstants";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import { StageDropdown } from "../../leads/components/StageDropdown";

const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const DesignTableView = ({
  designs,
  onDesignClick,
  onEditClick,
  loading,
  updateStatus,
  onToggleDisable,
  user,
  onRequestApproval
}) => {
  const getStatusVariant = (status) => STATUS_VARIANT_MAP[status?.toLowerCase()] || STATUS_VARIANT_MAP.default;

  const getApprovalStatus = (approval) => {
    if (!approval || !approval.isRequested) {
      return { text: "Not Requested", color: "text-slate-500 bg-slate-100" };
    }
    
    const designerStatus = approval.designer?.status || 'pending';
    const mainDesignerStatus = approval.mainDesigner?.status || 'pending';

    if (mainDesignerStatus === 'approved') return { text: "Fully Approved", color: "text-green-700 bg-green-100" };
    if (mainDesignerStatus === 'rejected') return { text: "Rejected by Main Designer", color: "text-red-700 bg-red-100" };
    if (designerStatus === 'rejected') return { text: "Rejected by Designer", color: "text-red-700 bg-red-100" };
    if (designerStatus === 'approved') return { text: "Pending Main Designer", color: "text-blue-700 bg-blue-100" };
    
    return { text: "Requested", color: "text-blue-700 bg-blue-100" };
  };

  const columns = useMemo(() => {
    return [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: "140px",
        render: (l) => (
          <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
            {l.customId || "—"}
          </span>
        )
      },
      {
        header: "Contact Details",
        key: "customerName",
        render: (l) => {
          const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
          return (
            <div className="flex items-center gap-2.5">
              <Avatar name={fullName} size="xs" />
              <div className="flex flex-col min-w-0">
                <span className="text-brand-dark tracking-tight text-sm font-medium truncate">
                  {fullName}
                </span>
                <span className="text-[10px] text-slate-400 truncate flex items-center gap-1 mt-0.5 tracking-tighter">
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="mx-1 opacity-50">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: "Last Note",
        key: "lastNote",
        className: "min-w-[200px]",
        render: (l) => {
          let displayNote = l.stageNotes?.design || "";
          if (displayNote && typeof displayNote === 'string' && displayNote.includes(' | Note: ')) {
            displayNote = displayNote.split(' | Note: ')[1].trim();
          }
          return (
            <span className="text-[11px] text-slate-600 italic block py-1 truncate max-w-[250px]">
              {displayNote || "—"}
            </span>
          );
        }
      },
      {
        header: "Media Count",
        key: "mediaCount",
        render: (l) => (
          <Badge
            variant={l.mediaCount > 0 ? "success" : "secondary"}
            label={`${l.mediaCount || 0} Files`}
            className="text-[10px] px-2 py-0.5"
          />
        )
      },
      {
        header: "Approval Status",
        key: "approvalStatus",
        render: (l) => {
          const status = getApprovalStatus(l.designApproval);
          return (
            <span className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider ${status.color}`}>
              {status.text}
            </span>
          );
        }
      },
      {
        header: "Actions",
        key: "actions",
        render: (l) => {
          const approval = l.designApproval || {};
          const isRequested = approval.isRequested;
          const isRejected = approval.designer?.status === 'rejected' || approval.mainDesigner?.status === 'rejected';
          const isFullyApproved = approval.mainDesigner?.status === 'approved';
          
          // Edit is disabled if a request has been sent, unless it was rejected.
          const isEditDisabled = isRequested && !isRejected;

          return (
            <div className="flex items-center gap-1.5 justify-end">
              <TableActionButton
                icon={Eye}
                variant="primary"
                onClick={(e) => { e.stopPropagation(); onDesignClick(l); }}
                title="View Design Details"
              />
              <TableActionButton
                icon={Edit2}
                variant={isEditDisabled ? "neutral" : "orange"}
                disabled={isEditDisabled}
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (!isEditDisabled) onEditClick(l); 
                }}
                title={isEditDisabled ? "Cannot edit while approval is pending" : "Update Design Media"}
              />
              <TableActionButton
                icon={Trash2}
                variant="danger"
                disabled={isEditDisabled}
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (!isEditDisabled && onToggleDisable) onToggleDisable(l); 
                }}
                title="Delete Design"
              />
              <TableActionButton
                icon={Download}
                variant={isFullyApproved ? "success" : "neutral"}
                disabled={!isFullyApproved}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isFullyApproved) {
                    // Logic to download design media
                    if (l.designMedia && l.designMedia.length > 0) {
                      l.designMedia.forEach(media => {
                        window.open(media.url, '_blank');
                      });
                    } else {
                      alert("No design media files found to download.");
                    }
                  }
                }}
                title={isFullyApproved ? "Download Design Media" : "Download available only after full approval"}
              />
            </div>
          );
        }
      }
    ];
  }, [onEditClick, onDesignClick, onRequestApproval]);

  return (
    <DataTable
      columns={columns}
      data={designs}
      backgroundImage={TABLE_BACKGROUNDS.designs}
      isRowInactive={(row) => row?.isDisabled}
      getRowClassName={(row) => row?.isDisabled ? "bg-slate-100/90 border-y border-slate-200/80" : "hover:bg-slate-50/50"}
      className="border-none shadow-none rounded-none"
    />
  );
};